import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { websitesAPI } from '../api/websites';
import './WebsiteSelector.css';

function WebsiteSelector() {
  const [websites, setWebsites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newWebsiteName, setNewWebsiteName] = useState('');
  const [newWebsiteDesc, setNewWebsiteDesc] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    loadWebsites();
  }, []);

  const loadWebsites = async () => {
    try {
      setLoading(true);
      const data = await websitesAPI.getAll();
      setWebsites(data);
    } catch (err) {
      setError(err.message);
      console.error('Failed to load websites:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateWebsite = async (e) => {
    e.preventDefault();
    if (!newWebsiteName.trim()) {
      alert('Please enter a website name');
      return;
    }

    try {
      const newWebsite = await websitesAPI.create(newWebsiteName, newWebsiteDesc);
      setWebsites([...websites, newWebsite]);
      setNewWebsiteName('');
      setNewWebsiteDesc('');
      setShowCreateModal(false);
      // Navigate to the editor for the new website
      navigate(`/editor/${newWebsite.id}`);
    } catch (err) {
      alert(err.message);
    }
  };

  const handleWebsiteClick = (websiteId) => {
    navigate(`/editor/${websiteId}`);
  };

  const handleDeleteWebsite = async (e, websiteId) => {
    e.stopPropagation();
    if (!window.confirm('Are you sure you want to delete this website? This will delete all content for this website.')) {
      return;
    }

    try {
      await websitesAPI.delete(websiteId);
      setWebsites(websites.filter(w => w.id !== websiteId));
    } catch (err) {
      alert(err.message);
    }
  };

  if (loading) {
    return (
      <div className="website-selector-container">
        <div className="loading">Loading websites...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="website-selector-container">
        <div className="error">Error: {error}</div>
      </div>
    );
  }

  return (
    <div className="website-selector-container">
      <div className="website-selector-header">
        <div className="app-logo" aria-hidden="true">LP</div>
        <h1>Landing Page Creator</h1>
        <p>Select a website to edit or create a new one</p>
      </div>

      <div className="websites-grid">
        {websites.map((website) => (
          <div
            key={website.id}
            className="website-card"
            onClick={() => handleWebsiteClick(website.id)}
          >
            <div className="website-card-header">
              <h3>{website.name}</h3>
              <button
                className="delete-btn"
                onClick={(e) => handleDeleteWebsite(e, website.id)}
                title="Delete website"
              >
                ×
              </button>
            </div>
            {website.description && (
              <p className="website-description">{website.description}</p>
            )}
            <div className="website-meta">
              <span>Created: {new Date(website.createdAt).toLocaleDateString()}</span>
            </div>
          </div>
        ))}

        <div
          className="website-card create-card"
          onClick={() => setShowCreateModal(true)}
        >
          <div className="create-card-content">
            <span className="create-icon">+</span>
            <h3>Create New Website</h3>
          </div>
        </div>
      </div>

      {showCreateModal && (
        <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Create New Website</h3>
            <form onSubmit={handleCreateWebsite}>
              <div className="form-group">
                <label htmlFor="website-name">Name:</label>
                <input
                  id="website-name"
                  type="text"
                  value={newWebsiteName}
                  onChange={(e) => setNewWebsiteName(e.target.value)}
                  placeholder="e.g., My Landing Page"
                  required
                  autoFocus
                />
              </div>
              <div className="form-group">
                <label htmlFor="website-desc">Description (optional):</label>
                <input
                  id="website-desc"
                  type="text"
                  value={newWebsiteDesc}
                  onChange={(e) => setNewWebsiteDesc(e.target.value)}
                  placeholder="Description"
                />
              </div>
              <div className="form-actions">
                <button type="submit">Create</button>
                <button type="button" onClick={() => setShowCreateModal(false)}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default WebsiteSelector;

